import { Route } from '@angular/router';
import { AuthGuard } from './core/auth/guards/auth.guard';
import { NoAuthGuard } from './core/auth/guards/noAuth.guard';
import { PasswordChangeGuard } from './core/auth/guards/password-change.guard';
import { SignOutResolver } from './core/auth/sign-out.resolver';
import { LayoutComponent } from './modules/pos-general/client-area/layout/layout.component';
import { SignInComponent } from './modules/pos-general/client-area/auth/sign-in/sign-in.component';
import { SelectOutletComponent } from './modules/pos-general/client-area/auth/select-outlet/select-outlet.component';
import { InitialDataResolver } from './app.resolvers';
import { environment } from '../environments/environment';

// Voucher Generator imports
import { VoucherGeneratorLayoutComponent } from './modules/voucher-generator/layout/layout.component';
import { VgSignInComponent } from './modules/voucher-generator/auth/sign-in/sign-in.component';
import { VgSelectOutletComponent } from './modules/voucher-generator/auth/select-outlet/select-outlet.component';

// =====================================================
// CLIENT AREA ROUTES (untuk kkomi-client.*.pos.awh.co.id)
// Menggunakan auth guards, layout dengan sidebar, dll
// =====================================================
const clientAreaAppRoutes: Route[] = [
    {
        path: '',
        pathMatch: 'full',
        redirectTo: 'dashboard',
    },
    {
        path: 'sign-in',
        component: SignInComponent,
        canActivate: [NoAuthGuard],
    },
    {
        path: 'select-outlet',
        component: SelectOutletComponent,
    },
    {
        path: 'sign-out',
        canActivate: [AuthGuard],
        resolve: { action: SignOutResolver },
        children: [],
    },
    {
        path: '',
        component: LayoutComponent,
        canActivate: [AuthGuard],
        canActivateChild: [PasswordChangeGuard],
        resolve: {
            initialData: InitialDataResolver,
        },
        children: [
            {
                path: 'dashboard',
                loadChildren: () => import('./modules/pos-general/client-area/dashboard/dashboard.module').then(m => m.DashboardModule)
            },
            {
                path: 'tenant-dashboard',
                loadChildren: () => import('./modules/pos-general/client-area/tenant-dashboard/tenant-dashboard.module').then(m => m.TenantDashboardModule)
            },
            {
                path: 'outlet-register',
                loadChildren: () => import('./modules/pos-general/client-area/outlet-register/outlet-register.module').then(m => m.OutletRegisterModule)
            },
            {
                path: 'products',
                loadChildren: () => import('./modules/pos-general/client-area/product/product.module').then(m => m.ProductModule)
            },
            {
                path: 'reports',
                loadChildren: () => import('./modules/pos-general/client-area/report/report.module').then(m => m.ReportModule)
            },
            {
                path: 'users',
                loadChildren: () => import('./modules/pos-general/client-area/user/user.module').then(m => m.UserModule)
            },
            {
                path: 'voucher',
                loadChildren: () => import('./modules/pos-general/client-area/voucher/voucher.module').then(m => m.VoucherModule)
            },
            {
                path: 'promo',
                loadChildren: () => import('./modules/pos-general/client-area/promo/promo.module').then(m => m.PromoModule)
            },
            {
                path: 'event',
                loadChildren: () => import('./modules/pos-general/client-area/event/event.module').then(m => m.EventModule)
            },
            {
                path: 'self-order',
                loadChildren: () => import('./modules/pos-general/client-area/self-order/self-order.module').then(m => m.SelfOrderModule)
            },
            {
                path: 'settings',
                loadChildren: () => import('./modules/pos-general/client-area/settings/settings.module').then(m => m.SettingsModule)
            }
        ]
    },
    // Fallback 404 untuk client area
    {
        path: '**',
        redirectTo: 'sign-in',
    },
];

// =====================================================
// POS KASIR ROUTES (untuk kkomi.*.pos.awh.co.id)
// Auth dan layout berbeda, fullscreen mode, tanpa sidebar
// =====================================================
const posKasirAppRoutes: Route[] = [
    {
        path: '',
        pathMatch: 'full',
        redirectTo: 'kasir',
    },
    // POS Kasir memiliki auth dan layout sendiri di dalam module-nya
    {
        path: 'kasir',
        loadChildren: () => import('./modules/pos-general/pos-kasir/pos-kasir.module').then(m => m.PosKasirModule)
    },
    // Fallback 404 untuk pos kasir
    {
        path: '**',
        redirectTo: 'kasir',
    },
];

// =====================================================
// WEB SELF-ORDER ROUTES (untuk kkomi-web.*.pos.awh.co.id)
// Customer self-ordering, mobile-only UI, guest mode
// =====================================================
const webAppRoutes: Route[] = [
    {
        path: '',
        loadChildren: () => import('./modules/pos-general/web/web.module').then(m => m.WebModule)
    },
    // Fallback 404 untuk web
    {
        path: '**',
        redirectTo: '',
    },
];

// =====================================================
// VOUCHER GENERATOR ROUTES (untuk kkomi-voucher.*.pos.awh.co.id)
// Generate & manage vouchers, layout mirip client area
// =====================================================
const voucherGeneratorAppRoutes: Route[] = [
    {
        path: '',
        pathMatch: 'full',
        redirectTo: 'dashboard',
    },
    {
        path: 'sign-in',
        component: VgSignInComponent,
        canActivate: [NoAuthGuard],
    },
    {
        path: 'select-outlet',
        component: VgSelectOutletComponent,
    },
    {
        path: 'sign-out',
        canActivate: [AuthGuard],
        resolve: { action: SignOutResolver },
        children: [],
    },
    {
        path: '',
        component: VoucherGeneratorLayoutComponent,
        canActivate: [AuthGuard],
        resolve: {
            initialData: InitialDataResolver,
        },
        children: [
            {
                path: 'dashboard',
                loadChildren: () => import('./modules/voucher-generator/dashboard/dashboard.module').then(m => m.VgDashboardModule)
            },
            {
                path: 'voucher',
                loadChildren: () => import('./modules/voucher-generator/voucher/voucher.module').then(m => m.VgVoucherModule)
            },
            {
                path: 'pengguna',
                loadChildren: () => import('./modules/voucher-generator/user/user.module').then(m => m.VgUserModule)
            }
        ]
    },
    // Fallback 404 untuk voucher generator
    {
        path: '**',
        redirectTo: 'sign-in',
    },
];

// =====================================================
// POS LAUNDRY KASIR ROUTES 
// =====================================================
const posLaundryKasirAppRoutes: Route[] = [
    {
        path: '',
        pathMatch: 'full',
        redirectTo: 'kasir',
    },
    {
        path: 'kasir',
        loadChildren: () => import('./modules/pos-laundry/pos-kasir/pos-kasir.module').then(m => m.PosLaundryKasirModule)
    },
    {
        path: '**',
        redirectTo: 'kasir',
    },
];

// =====================================================
// POS PHOTOBOOTH KASIR ROUTES 
// =====================================================
const posPhotoboothKasirAppRoutes: Route[] = [
    {
        path: '',
        pathMatch: 'full',
        redirectTo: 'kasir',
    },
    {
        path: 'kasir',
        loadChildren: () => import('./modules/pos-photobooth/pos-kasir/pos-kasir.module').then(m => m.PosPhotoboothKasirModule)
    },
    {
        path: '**',
        redirectTo: 'kasir',
    },
];


// =====================================================
// POS LAUNDRY CLIENT AREA ROUTES
// =====================================================
import { LaundryClientAreaComponent } from './modules/pos-laundry/client-area/layout/layout.component';
import { SignInComponent as LaundrySignInComponent } from './modules/pos-laundry/client-area/auth/sign-in/sign-in.component';
import { SelectOutletComponent as LaundrySelectOutletComponent } from './modules/pos-laundry/client-area/auth/select-outlet/select-outlet.component';

const posLaundryClientAreaAppRoutes: Route[] = [
    {
        path: '',
        pathMatch: 'full',
        redirectTo: 'dashboard',
    },
    {
        path: 'sign-in',
        component: LaundrySignInComponent,
        canActivate: [NoAuthGuard],
    },
    {
        path: 'select-outlet',
        component: LaundrySelectOutletComponent,
    },
    {
        path: 'sign-out',
        canActivate: [AuthGuard],
        resolve: { action: SignOutResolver },
        children: [],
    },
    {
        path: '',
        component: LaundryClientAreaComponent,
        canActivate: [AuthGuard],
        canActivateChild: [PasswordChangeGuard],
        resolve: {
            initialData: InitialDataResolver,
        },
        children: [
            {
                path: 'dashboard',
                loadChildren: () => import('./modules/pos-laundry/client-area/dashboard/dashboard.module').then(m => m.DashboardModule)
            },
            {
                path: 'tenant-dashboard',
                loadChildren: () => import('./modules/pos-laundry/client-area/tenant-dashboard/tenant-dashboard.module').then(m => m.TenantDashboardModule)
            },
            {
                path: 'outlet-register',
                loadChildren: () => import('./modules/pos-laundry/client-area/outlet-register/outlet-register.module').then(m => m.OutletRegisterModule)
            },
            {
                path: 'products',
                loadChildren: () => import('./modules/pos-laundry/client-area/product/product.module').then(m => m.ProductModule)
            },
            {
                path: 'reports',
                loadChildren: () => import('./modules/pos-laundry/client-area/report/report.module').then(m => m.ReportModule)
            },
            {
                path: 'users',
                loadChildren: () => import('./modules/pos-laundry/client-area/user/user.module').then(m => m.UserModule)
            },
            {
                path: 'voucher',
                loadChildren: () => import('./modules/pos-laundry/client-area/voucher/voucher.module').then(m => m.VoucherModule)
            },
            {
                path: 'promo',
                loadChildren: () => import('./modules/pos-laundry/client-area/promo/promo.module').then(m => m.PromoModule)
            },
            {
                path: 'event',
                loadChildren: () => import('./modules/pos-laundry/client-area/event/event.module').then(m => m.EventModule)
            },
            {
                path: 'self-order',
                loadChildren: () => import('./modules/pos-laundry/client-area/self-order/self-order.module').then(m => m.SelfOrderModule)
            },
            {
                path: 'settings',
                loadChildren: () => import('./modules/pos-laundry/client-area/settings/settings.module').then(m => m.SettingsModule)
            },
            {
                path: 'integration',
                loadChildren: () => import('./modules/pos-laundry/client-area/integration/integration.module').then(m => m.IntegrationModule)
            }
        ]
    },
    {
        path: '**',
        redirectTo: 'sign-in',
    },
];

// =====================================================
// POS LAUNDRY CUSTOMER ROUTES 
// =====================================================
const posLaundryCustomerAppRoutes: Route[] = [
    {
        path: '',
        loadChildren: () => import('./modules/pos-laundry/customer-detail/customer-detail.module').then(m => m.CustomerDetailModule)
    },
    {
        path: '**',
        redirectTo: '',
    },
];

// =====================================================
// POS PHOTOBOOTH CLIENT AREA ROUTES
// =====================================================
import { PhotoboothClientAreaComponent } from './modules/pos-photobooth/client-area/layout/layout.component';
import { SignInComponent as PhotoboothSignInComponent } from './modules/pos-photobooth/client-area/auth/sign-in/sign-in.component';
import { SelectOutletComponent as PhotoboothSelectOutletComponent } from './modules/pos-photobooth/client-area/auth/select-outlet/select-outlet.component';

const posPhotoboothClientAreaAppRoutes: Route[] = [
    {
        path: '',
        pathMatch: 'full',
        redirectTo: 'dashboard',
    },
    {
        path: 'sign-in',
        component: PhotoboothSignInComponent,
        canActivate: [NoAuthGuard],
    },
    {
        path: 'select-outlet',
        component: PhotoboothSelectOutletComponent,
    },
    {
        path: 'sign-out',
        canActivate: [AuthGuard],
        resolve: { action: SignOutResolver },
        children: [],
    },
    {
        path: '',
        component: PhotoboothClientAreaComponent,
        canActivate: [AuthGuard],
        canActivateChild: [PasswordChangeGuard],
        resolve: {
            initialData: InitialDataResolver,
        },
        children: [
            {
                path: 'dashboard',
                loadChildren: () => import('./modules/pos-photobooth/client-area/dashboard/dashboard.module').then(m => m.DashboardModule)
            },
            {
                path: 'dashboard-voucher',
                loadChildren: () => import('./modules/pos-photobooth/client-area/dashboard-voucher/dashboard-voucher.module').then(m => m.DashboardVoucherModule)
            },
            {
                path: 'tenant-dashboard',
                loadChildren: () => import('./modules/pos-photobooth/client-area/tenant-dashboard/tenant-dashboard.module').then(m => m.TenantDashboardModule)
            },
            {
                path: 'outlet-register',
                loadChildren: () => import('./modules/pos-photobooth/client-area/outlet-register/outlet-register.module').then(m => m.OutletRegisterModule)
            },
            {
                path: 'products',
                loadChildren: () => import('./modules/pos-photobooth/client-area/product/product.module').then(m => m.ProductModule)
            },
            {
                path: 'reports',
                loadChildren: () => import('./modules/pos-photobooth/client-area/report/report.module').then(m => m.ReportModule)
            },
            {
                path: 'users',
                loadChildren: () => import('./modules/pos-photobooth/client-area/user/user.module').then(m => m.UserModule)
            },
            {
                path: 'voucher',
                loadChildren: () => import('./modules/pos-photobooth/client-area/voucher/voucher.module').then(m => m.VoucherModule)
            },
            {
                path: 'promo',
                loadChildren: () => import('./modules/pos-photobooth/client-area/promo/promo.module').then(m => m.PromoModule)
            },
            {
                path: 'event',
                loadChildren: () => import('./modules/pos-photobooth/client-area/event/event.module').then(m => m.EventModule)
            },
            {
                path: 'self-order',
                loadChildren: () => import('./modules/pos-photobooth/client-area/self-order/self-order.module').then(m => m.SelfOrderModule)
            },
            {
                path: 'settings',
                loadChildren: () => import('./modules/pos-photobooth/client-area/settings/settings.module').then(m => m.SettingsModule)
            },
            {
                path: 'content-categories',
                loadChildren: () => import('./modules/pos-photobooth/client-area/content-category/content-category.module').then(m => m.ContentCategoryModule)
            }
        ]
    },
    {
        path: '**',
        redirectTo: 'sign-in',
    },
];


// =====================================================
// POS ITS KASIR ROUTES (THEME PARK)
// =====================================================
const posItsKasirAppRoutes: Route[] = [
    {
        path: '',
        pathMatch: 'full',
        redirectTo: 'kasir',
    },
    {
        path: 'kasir',
        loadChildren: () => import('./modules/pos-its/pos-kasir/pos-kasir.module').then(m => m.PosItsKasirModule)
    },
    {
        path: '**',
        redirectTo: 'kasir',
    },
];

// =====================================================
// POS ITS CLIENT AREA ROUTES (THEME PARK)
// =====================================================
import { ItsClientAreaComponent } from './modules/pos-its/client-area/layout/layout.component';
import { SignInComponent as ItsSignInComponent } from './modules/pos-its/client-area/auth/sign-in/sign-in.component';
import { SelectOutletComponent as ItsSelectOutletComponent } from './modules/pos-its/client-area/auth/select-outlet/select-outlet.component';

const posItsClientAreaAppRoutes: Route[] = [
    {
        path: '',
        pathMatch: 'full',
        redirectTo: 'dashboard',
    },
    {
        path: 'sign-in',
        component: ItsSignInComponent,
        canActivate: [NoAuthGuard],
    },
    {
        path: 'select-outlet',
        component: ItsSelectOutletComponent,
    },
    {
        path: 'sign-out',
        canActivate: [AuthGuard],
        resolve: { action: SignOutResolver },
        children: [],
    },
    {
        path: '',
        component: ItsClientAreaComponent,
        canActivate: [AuthGuard],
        canActivateChild: [PasswordChangeGuard],
        resolve: {
            initialData: InitialDataResolver,
        },
        children: [
            {
                path: 'dashboard',
                loadChildren: () => import('./modules/pos-its/client-area/dashboard/dashboard.module').then(m => m.DashboardModule)
            },
            {
                path: 'tenant-dashboard',
                loadChildren: () => import('./modules/pos-its/client-area/tenant-dashboard/tenant-dashboard.module').then(m => m.TenantDashboardModule)
            },
            {
                path: 'outlet-register',
                loadChildren: () => import('./modules/pos-its/client-area/outlet-register/outlet-register.module').then(m => m.OutletRegisterModule)
            },
            {
                path: 'products',
                loadChildren: () => import('./modules/pos-its/client-area/product/product.module').then(m => m.ProductModule)
            },
            {
                path: 'reports',
                loadChildren: () => import('./modules/pos-its/client-area/report/report.module').then(m => m.ReportModule)
            },
            {
                path: 'users',
                loadChildren: () => import('./modules/pos-its/client-area/user/user.module').then(m => m.UserModule)
            },
            {
                path: 'voucher',
                loadChildren: () => import('./modules/pos-its/client-area/voucher/voucher.module').then(m => m.VoucherModule)
            },
            {
                path: 'promo',
                loadChildren: () => import('./modules/pos-its/client-area/promo/promo.module').then(m => m.PromoModule)
            },
            {
                path: 'event',
                loadChildren: () => import('./modules/pos-its/client-area/event/event.module').then(m => m.EventModule)
            },
            {
                path: 'self-order',
                loadChildren: () => import('./modules/pos-its/client-area/self-order/self-order.module').then(m => m.SelfOrderModule)
            },
            {
                path: 'settings',
                loadChildren: () => import('./modules/pos-its/client-area/settings/settings.module').then(m => m.SettingsModule)
            }
        ]
    },
    {
        path: '**',
        redirectTo: 'sign-in',
    },
];

// =====================================================
// PILIH ROUTES BERDASARKAN APP TYPE
// =====================================================
function getAppRoutes(): Route[] {
    switch (environment.appType) {
        case 'pos-kasir':
            return posKasirAppRoutes;
        case 'pos-laundry-kasir':
            return posLaundryKasirAppRoutes;
        case 'pos-photobooth-kasir':
            return posPhotoboothKasirAppRoutes;
        case 'pos-laundry-client-area':
            return posLaundryClientAreaAppRoutes;
        case 'pos-laundry-customer':
            return posLaundryCustomerAppRoutes;
        case 'pos-photobooth-client-area':
            return posPhotoboothClientAreaAppRoutes;
        case 'web':
            return webAppRoutes;
                case 'pos-its-kasir':
            return posItsKasirAppRoutes;
        case 'pos-its-client-area':
            return posItsClientAreaAppRoutes;
        case 'voucher-generator':
            return voucherGeneratorAppRoutes;
        default:
            return clientAreaAppRoutes;
    }
}

export const appRoutes: Route[] = getAppRoutes();
